import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Table, Tooltip, Spin, Tag } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Swal from 'sweetalert2';
import Fuse from 'fuse.js';

import { CheckCircleOutlined } from '@ant-design/icons';
import { BiExport } from 'react-icons/bi';
import { FaSearch, FaEye, FaArchive, FaPlus } from 'react-icons/fa';
import { FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

dayjs.extend(utc);

const API_URL = process.env.REACT_APP_API_URL || '';

const paymentStatusFilters = [
    { text: 'Successful', value: 'successful' },
    { text: 'Pending', value: 'pending' },
    { text: 'Failed', value: 'failed' },
    { text: 'Cancelled', value: 'cancelled' },
    { text: 'Expired', value: 'expired' },
];

const paymentMethodFilters = [
    { text: 'GCash', value: 'GCASH' },
    { text: 'Maya', value: 'MAYA' },
    { text: 'Bank Transfer', value: 'BANK_TRANSFER' },
    { text: 'Over The Counter', value: 'OVER_THE_COUNTER' },
];

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return 'N/A';
    return Number(value).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs.utc(date).format('MMM DD, YYYY hh:mm A');
};

const ManagePayments = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const [dataSource, setDataSource] = useState([]);
    const [rawApiData, setRawApiData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [showArchived, setShowArchived] = useState(false);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchPayments = useCallback(async (isArchived) => {
        setLoading(true);
        const apiUrl = `${API_URL}/api/admin/paymentHistory?showArchived=${isArchived}&limit=10000`;
        try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
                throw new Error(errorData.message);
            }
            const result = await response.json();
            const data = result.data.map(item => ({ ...item, key: item._id })) || [];
            setRawApiData(data);
            setDataSource(data);
        } catch (error) {
            console.error("Error fetching payments:", error);
            toast.error(`Failed to fetch payments: ${error.message}`);
            setRawApiData([]);
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPayments(showArchived);
    }, [showArchived, fetchPayments]);

    useEffect(() => {
        let filteredData = [...rawApiData];
        if (searchTerm.trim()) {
            const fuseOptions = {
                keys: ['applicantName', 'description', 'referenceNumber', 'status', 'paymentMethod'],
                threshold: 0.4,
            };
            const fuse = new Fuse(filteredData, fuseOptions);
            filteredData = fuse.search(searchTerm).map(result => result.item);
        }
        setDataSource(filteredData);
    }, [searchTerm, rawApiData]);

    const handleCreate = () => navigate('/user-admin/payments/create');

    const handleArchiveToggle = (record) => {
        const action = record.isArchived ? 'unarchive' : 'archive';
        Swal.fire({
            title: `Confirm ${action}`,
            text: `Are you sure you want to ${action} this payment?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Yes, ${action} it!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const response = await fetch(`${API_URL}/api/admin/paymentHistory/${record._id}/archive`, { method: 'PATCH' });
                    const resData = await response.json();
                    if (!response.ok) throw new Error(resData.message || `Failed to ${action}`);
                    Swal.fire('Success!', `The payment has been successfully ${action}d.`, 'success');
                    fetchPayments(showArchived);
                } catch (error) {
                    Swal.fire('Error', error.message, 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleValidatePayment = (record) => {
        const adminName = localStorage.getItem('fullName');
        if (!adminName) {
            toast.error("Could not identify admin. Please log in again.");
            return;
        }

        Swal.fire({
            title: 'Confirm Validation',
            html: `This will mark the payment as validated.<br/><br/>
                   <strong>Payer:</strong> ${record.applicantName}<br/>
                   <strong>Amount:</strong> ${formatCurrency(record.amount)}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, validate it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const performValidation = async () => {
                    setLoading(true);
                    try {
                        const response = await fetch(`${API_URL}/api/admin/paymentHistory/${record._id}/validate`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ adminName }),
                        });
                        const resData = await response.json();
                        if (!response.ok) throw new Error(resData.message || 'Failed to validate payment');
                        Swal.fire('Validated!', 'The payment has been successfully validated.', 'success');
                        fetchPayments(showArchived);
                        createLog('Update', `Validated payment with Ref No: ${record.referenceNumber}`);
                    } catch (error) {
                        Swal.fire('Validation Failed', error.message, 'error');
                    } finally {
                        setLoading(false);
                    }
                };
                performValidation();
            }
        });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setTableFilters(filters);
        setSorter(sorter);
    };

    const handleSearchChange = (e) => setSearchTerm(e.target.value);

    const handleClearFiltersAndSearch = () => {
        setTableFilters({});
        setSorter({});
        setSearchTerm('');
    };

    const handleRefresh = () => fetchPayments(showArchived);

    const handleViewPaymentDetails = (record) => navigate(`/admin/payment-history/view/${record._id}`);

    const createLog = async (action, detail) => {
        try {
            const userID = localStorage.getItem('userID');
            const accountName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            if (!userID || !accountName || !role) {
                console.warn('User info not found for logging.');
                return;
            }
            const logData = { userID, accountName, role, action, detail };
            await fetch(`${API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData),
            });
        } catch (error) {
            console.error('Failed to record system log:', error);
        }
    };
    const handleExport = () => { /* ... existing code ... */ };

    const columns = [
        {
            title: 'Payment Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: formatDate,
            sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
            sortOrder: sorter.columnKey === 'createdAt' && sorter.order,
        },
        {
            title: 'Payer Name',
            dataIndex: 'applicantName',
            key: 'applicantName',
            width: 250,
            sorter: (a, b) => a.applicantName.localeCompare(b.applicantName),
            sortOrder: sorter.columnKey === 'applicantName' && sorter.order,
            ellipsis: true,
            render: (text) => <Tooltip title={text}>{text || 'N/A'}</Tooltip>,
        },
        { title: 'Description', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
        { title: 'Reference No.', dataIndex: 'referenceNumber', key: 'referenceNumber', width: 200, ellipsis: true, render: (text) => <Tooltip title={text}>{text}</Tooltip> },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 150,
            align: 'right',
            render: formatCurrency,
            sorter: (a, b) => a.amount - b.amount,
            sortOrder: sorter.columnKey === 'amount' && sorter.order,
        },
        {
            title: 'Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            width: 150,
            filters: paymentMethodFilters,
            onFilter: (value, record) => record.paymentMethod.includes(value),
            filteredValue: tableFilters.paymentMethod || null,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            fixed: 'right',
            filters: paymentStatusFilters,
            onFilter: (value, record) => record.status.includes(value),
            filteredValue: tableFilters.status || null,
            render: (status) => { let color; switch (status) { case 'successful': color = 'green'; break; case 'pending': color = 'orange'; break; default: color = 'red'; } return <Tag color={color}>{status?.toUpperCase()}</Tag>; }
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 200,
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <Button icon={<FaEye />} style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} onClick={() => handleViewPaymentDetails(record)}>
                        View Details
                    </Button>

                    {record.isArchived ? (
                        <Button icon={<FaBoxOpen />} style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} onClick={() => handleArchiveToggle(record)}>
                            Unarchive
                        </Button>
                    ) : (
                        <>
                            {record.status === 'successful' && !record.validatedBy ? (
                                <Button type="primary" icon={<CheckCircleOutlined />} style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} onClick={() => handleValidatePayment(record)}>
                                    Validate
                                </Button>
                            ) : (
                                <Button icon={<FaArchive />} style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} onClick={() => handleArchiveToggle(record)}>
                                    Archive
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )
        },
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {showArchived && (
                <Tag className="archived-tag" color="volcano">
                    Viewing <strong>Archived Payments</strong>
                </Tag>
            )}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title"><p className="heading">{showArchived ? "Archived Payments" : "Manage Payments"}</p></div>
                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFiltersAndSearch}>Clear</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <Input
                                placeholder="Search by Name, Ref No, Desc..."
                                allowClear
                                style={{ width: 300 }}
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <Button
                                icon={showArchived ? <FaBoxOpen /> : <FaArchive />}
                                onClick={() => setShowArchived(prev => !prev)}
                            >
                                {showArchived ? "View Active" : "View Archive"}
                            </Button>
                            <Button type="ghost" className="create-btn" icon={<FaPlus />} onClick={handleCreate}>
                                Create Payment
                            </Button>
                        </div>
                    </div>
                    <Spin spinning={loading}>
                        <Table
                            className="custom-table"
                            columns={columns}
                            dataSource={dataSource}
                            scroll={{ x: 1650 }}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            bordered
                            onChange={handleTableChange}
                            rowKey="_id"
                        />
                    </Spin>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ManagePayments;